import {Injectable, OnDestroy} from "@angular/core";
import {AnalyticsService} from "../orchestration/analytics.service";
import {ParamConfigService} from "../config-support/param-config.service";
import {ResponsiveService} from "../orchestration/responsive.service";
import {SessionModel} from "../orchestration/session.model";
import {
    CardPaymentSubmissionFailure,
    CardPaymentSubmissionResult,
    IntegratedPaymentProvider,
    PaymentCard,
    PaymentType,
    ThreeDSResult,
    WalletPaymentErrorType,
    WalletPaymentSubmissionFailure,
    WalletPaymentSubmissionResult,
    WalletProvider
} from "./payment";
import {NavigationService} from "../orchestration/navigation.service";
import {LoadingService} from "../ui-elements/loading/loading.service";
import {Observable, Subject, Subscription} from "rxjs";
import {OrderEvents, OrderInitiationStatus, OrderSubmissionStatus} from "../order/order-events";
import {OrderingPhase, SessionEvents} from "../orchestration/session-events";
import {OrderPaymentDetailsData, OrderPaymentDetailsEntityModel} from "./order-payment-details-entity.model";
import {CustomerDetailsService, PaymentCustomerDetails} from "../customer-info/customer-details.service";
import {MessageDetails, MessageLevel} from "../notification/message/message";
import {NotificationService} from "../notification/notification.service";
import {OrderingContextEvents} from "../ordering-context/ordering-context-events";
import {PaymentEvents} from "./payment-events";
import {OrderingContextModel} from "../ordering-context/ordering-context.model";
import {OrderModel} from "../order/order.model";
import {PaymentAuthorisationData} from "../order/order-placement.service";
import {EntityData} from "@fedelta-pos/data-client";
import {PaymentSurchargeData} from "../order/order-initiation.service";
import {WalletPaymentProcessor} from "./wallet-payment/wallet-payment-processor";
import {StripeWalletPaymentProcessor} from "./wallet-payment/stripe-wallet-payment-processor";
import {TyroWalletPaymentProcessor} from "./wallet-payment/tyro-wallet-payment-processor";
import {AdyenWalletPaymentProcessor} from "./wallet-payment/adyen-wallet-payment-processor";

@Injectable()
export class PaymentModel implements OnDestroy {
    public paymentProvider: IntegratedPaymentProvider;
    public paymentPlatformPublicKey: string;
    public paymentPlatformLiveMode: boolean;
    public paymentMethods: string;
    public requestPayerName: boolean;
    public requestPayerEmail: boolean;
    public requestPayerPhone: boolean;
    public requestPayerPostcode: boolean;
    public walletPaymentProcessor: WalletPaymentProcessor;
    private walletPaymentProcessorCreationRequested$: Subject<void> = new Subject<void>();
    private walletPaymentProcessorCreationDebouncer: Observable<void> = this.walletPaymentProcessorCreationRequested$.debounceTime(1000);

    // Avoid injection dependency on OrderModel
    private _orderModel: OrderModel;

    private subscriptions: Subscription = new Subscription();

    constructor(public navigationService: NavigationService,
                public analyticsService: AnalyticsService,
                public paramConfigService: ParamConfigService,
                public responsiveService: ResponsiveService,
                public loadingService: LoadingService,
                public sessionModel: SessionModel,
                public orderingContextModel: OrderingContextModel,
                public orderPaymentDetailsModel: OrderPaymentDetailsEntityModel,
                public customerDetailsService: CustomerDetailsService,
                public notificationService: NotificationService) {
        console.log("--- PaymentModel");

        this.subscriptions.add(this.walletPaymentProcessorCreationDebouncer.subscribe(() => this.createWalletPaymentProcessor()));

        this.subscriptions.add(OrderingContextEvents.storeDetailsChanged$.subscribe(async (storeChanged: boolean) => storeChanged && this.loadCustomerPaymentDetails("Store changed")));

        this.subscriptions.add(SessionEvents.currentApp$.subscribe(() => this.requestWalletPaymentProcessorCreation("App changed")));
        this.subscriptions.add(OrderingContextEvents.orderType$.subscribe(() => this.requestWalletPaymentProcessorCreation("Order type changed")));
        this.subscriptions.add(OrderingContextEvents.marketingOptIn$.subscribe(() => this.requestWalletPaymentProcessorCreation("Marketing opt-in changed")));

        // For tyro, an order initiation from the payment page will create a new payment secret - the old one must be discarded.
        this.subscriptions.add(OrderEvents.orderInitiationStatus$.subscribe((orderInitiationStatus: OrderInitiationStatus) => {
            if (orderInitiationStatus === OrderInitiationStatus.INITIATING && this.paymentProvider === IntegratedPaymentProvider.TYRO && SessionEvents.orderingPhase$.value === OrderingPhase.PAYMENT) {
                this.paymentPlatformPublicKey = undefined;
            }
        }));
    }

    set orderModel(orderModel: OrderModel) {
        this._orderModel = orderModel;
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    public async loadCustomerPaymentDetails(reason: string): Promise<void> {
        console.log("loading customer payment details:", reason);
        try {
            // Load payment details without a customer id first.  This will determine the customer scope (provider and live-mode setting).
            await this.orderPaymentDetailsModel.loadEntity(null);
            this.customerDetailsService.setPaymentScope(this.orderPaymentDetailsModel.data);

            // Find a persisted customer
            const paymentCustomerDetails: PaymentCustomerDetails = this.customerDetailsService.loadPaymentCustomer();

            // If we have a customer, load the payment details again to obtain the card details.
            if (paymentCustomerDetails) {
                await this.orderPaymentDetailsModel.loadEntity(paymentCustomerDetails.paymentCustomerId);
            }

            this.applyPaymentDetails(this.orderPaymentDetailsModel.data);
        } catch (error) {
            if (error.displayable) {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, error.data.messageKey, error.data.messageParams));
            } else {
                this.notificationService.displayFooterMessage(new MessageDetails(MessageLevel.ERROR, "woa.error.common.cannotCommunicateServer"));
            }
            throw error;
        }

        this.requestWalletPaymentProcessorCreation(reason);
    }

    public applyPaymentDetails(paymentDetails: OrderPaymentDetailsData) {
        let changed: boolean = false;
        if (paymentDetails.platformPublicKey && paymentDetails.platformPublicKey !== this.paymentPlatformPublicKey) {
            this.paymentPlatformPublicKey = paymentDetails.platformPublicKey;
            changed = true;
        }
        if (paymentDetails.provider && paymentDetails.provider !== this.paymentProvider) {
            this.paymentProvider = paymentDetails.provider;
            changed = true;
        }
        if (paymentDetails.platformLiveMode != undefined && paymentDetails.platformLiveMode !== this.paymentPlatformLiveMode) {
            this.paymentPlatformLiveMode = paymentDetails.platformLiveMode;
            changed = true;
        }
        if (paymentDetails.paymentMethods != undefined && paymentDetails.paymentMethods !== this.paymentMethods) {
            this.paymentMethods = paymentDetails.paymentMethods;
            changed = true;
        }
        if (changed) {
            PaymentEvents.paymentDetailsApplied$.next();
            this.requestWalletPaymentProcessorCreation("New payment details");
        } else {
            PaymentEvents.walletPaymentProcessorCreationResolved$.next(true);
        }
    }

    public get canMakeWalletPayment(): boolean {
        return this._orderModel.paymentRequired && !!this.walletPaymentProcessor;
    }

    public get canMakeGooglePayPayment(): boolean {
        return this.canMakeWalletPayment && this.walletPaymentProcessor.walletProvider === WalletProvider.GOOGLE_PAY;
    }

    public get canMakeApplePayPayment(): boolean {
        return this.canMakeWalletPayment && this.walletPaymentProcessor.walletProvider === WalletProvider.APPLE_PAY;
    }

    public canTip(paymentType: PaymentType): boolean {
        return paymentType === PaymentType.SAVED_CREDIT_CARD || paymentType === PaymentType.NEW_CREDIT_CARD || paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY;
    }

    public get hasSavedCreditCard(): boolean {
        return !!this.orderPaymentDetailsModel.data.cardLast4 && this.customerDetailsService.hasSavedPaymentCustomer;
    }

    public get cardBrand(): string {
        return this.orderPaymentDetailsModel.data.cardBrand;
    }

    public get cardCountry(): string {
        return this.orderPaymentDetailsModel.data.cardCountry;
    }

    public get cardLast4(): string {
        return this.orderPaymentDetailsModel.data.cardLast4;
    }

    public discardCard(): void {
        this.orderPaymentDetailsModel.data.cardLast4 = undefined;
    }

    private requestWalletPaymentProcessorCreation(reason: string): void {
        console.log(Date.now(), "Wallet payment processor creation requested:", reason);
        PaymentEvents.walletPaymentProcessorCreationResolved$.next(false);
        this.walletPaymentProcessorCreationRequested$.next();
    }

    private async createWalletPaymentProcessor(): Promise<void> {
        console.log("Creating wallet payment processor");
        this.requestPayerName = !this.orderingContextModel.firstNameTrusted && (this.orderingContextModel.showName || this.orderingContextModel.marketingOptIn);
        this.requestPayerEmail = !this.orderingContextModel.emailTrusted && (this.orderingContextModel.showEmail || (this.orderingContextModel.marketingOptIn && this.sessionModel.currentApp.collectMarketingEmail));
        this.requestPayerPhone = !this.orderingContextModel.mobilePhoneTrusted && (this.orderingContextModel.showPhoneNumber || (this.orderingContextModel.marketingOptIn && this.sessionModel.currentApp.collectMarketingMobilePhone));
        this.requestPayerPostcode = this.orderingContextModel.marketingOptIn && this.sessionModel.currentApp.collectMarketingPostCode;
        this.walletPaymentProcessor =
            this.responsiveService.isCordova ?
                undefined :
                /*
                (this.responsiveService.isAndroid ? new CordovaGooglePayProcessor(this) :
                    this.responsiveService.isIos ? new CordovaApplePayProcessor(this) :
                        undefined) :
                */
                this.paymentProvider === IntegratedPaymentProvider.STRIPE ? new StripeWalletPaymentProcessor(this) :
                    this.paymentProvider === IntegratedPaymentProvider.TYRO ? new TyroWalletPaymentProcessor(this) :
                        this.paymentProvider === IntegratedPaymentProvider.OOLIO ? new AdyenWalletPaymentProcessor(this) :
                            undefined;
        if (this.walletPaymentProcessor) {
            this.walletPaymentProcessor = await this.walletPaymentProcessor.init();
        }

        PaymentEvents.walletPaymentProcessorCreationResolved$.next(true);

        // noinspection ES6MissingAwait
        this.analyticsService.trackWalletPaymentStatus(!!this.walletPaymentProcessor);
    }

    /**
     * Allows the caller to wait for the wallet payment processor creation to be resolved.
     */
    public untilWalletPaymentProcessorCreationResolved(): Promise<void> {
        return new Promise<void>((resolve) => {
            const sub: Subscription = PaymentEvents.walletPaymentProcessorCreationResolved$.delay(0).subscribe((resolved: boolean) => {
                if (resolved) {
                    sub.unsubscribe();
                    resolve();
                }
            });
        });
    }

    public async authorisePayment(paymentType: PaymentType, paymentCard: PaymentCard, paymentAuthorisation: PaymentAuthorisationData, cardValidationFailed: () => void): Promise<PaymentAuthorisationData> {

        // Nothing to do if this is a 3DS or error retry
        if (paymentAuthorisation && (paymentAuthorisation.isThreeDS || paymentAuthorisation.token)) {
            return paymentAuthorisation;
        }

        if (!paymentAuthorisation) {
            paymentAuthorisation = <PaymentAuthorisationData>EntityData.create(PaymentAuthorisationData);
            paymentAuthorisation.paymentType = paymentType;
            paymentAuthorisation.storePaymentDetails = paymentType === PaymentType.NEW_CREDIT_CARD && paymentCard.canSavePaymentDetails();

            const paymentCustomerDetails: PaymentCustomerDetails = paymentType === PaymentType.GROUP_PAY ? undefined : this.customerDetailsService.loadPaymentCustomer();
            if (paymentCustomerDetails) {
                paymentAuthorisation.paymentCustomerId = paymentCustomerDetails.paymentCustomerId;
                paymentAuthorisation.paymentCustomerCreated = paymentCustomerDetails.created;
            }

            // Amount without surcharges and tips
            paymentAuthorisation.amount = this._orderModel.initiatedOrder.orderTotal;

            if (paymentType === PaymentType.POINTS) {
                paymentAuthorisation.payWithPoints = true;
            } else {
                const paymentSurcharge: PaymentSurchargeData = this._orderModel.initiatedOrder.getPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE);
                paymentAuthorisation.paymentSurchargeAmount = paymentSurcharge ? paymentSurcharge.amount : undefined;

                // For tyro, the tip amount has to match what was sent with the order initiation, since that's when the payment intent is created.
                paymentAuthorisation.tipAmount = this.paymentProvider === IntegratedPaymentProvider.TYRO ? this._orderModel.initiatedOrder.tipAmount : this._orderModel.tipAmount;
            }

            if (paymentType === PaymentType.SAVED_CREDIT_CARD) {
                paymentAuthorisation.paymentCardBrand = this.cardBrand;
                paymentAuthorisation.paymentCardCountry = this.cardCountry;
                paymentAuthorisation.paymentCardLast4 = this.cardLast4;
            } else {
                paymentAuthorisation.paymentIntentId = this._orderModel.initiatedOrder.paymentDetails.paymentIntentId;
            }

            // Browser info for 3DS (required for Adyen stored card payments)
            paymentAuthorisation.origin = window.location.origin;
            paymentAuthorisation.browserInfo.acceptHeader = "*/*";
            paymentAuthorisation.browserInfo.colorDepth = screen.colorDepth;
            paymentAuthorisation.browserInfo.javaEnabled = false;
            paymentAuthorisation.browserInfo.javaScriptEnabled = true;
            paymentAuthorisation.browserInfo.language = navigator.language;
            paymentAuthorisation.browserInfo.screenHeight = screen.height;
            paymentAuthorisation.browserInfo.screenWidth = screen.width;
            paymentAuthorisation.browserInfo.timeZoneOffset = new Date().getTimezoneOffset();
            paymentAuthorisation.browserInfo.userAgent = navigator.userAgent;
        }

        if (paymentType === PaymentType.NEW_CREDIT_CARD) {
            try {
                const submissionResult: CardPaymentSubmissionResult | CardPaymentSubmissionFailure = await paymentCard.submit();
                if (submissionResult instanceof CardPaymentSubmissionFailure) {
                    this.loadingService.dismissSpinner();
                    if (submissionResult.cardValidationFailure && cardValidationFailed) {
                        cardValidationFailed();
                    }
                    if (submissionResult.notifiable) {
                        // noinspection ES6MissingAwait
                        this.analyticsService.trackPlaceOrderFailed(submissionResult.errorMessage, submissionResult.errorCode, submissionResult.gatewayCode);
                        this.notificationService.logErrorToServer("AppOrderModel.placeOrder - payment authorisation failed", undefined, submissionResult);
                        await this.notificationService.displayAlert("woa.message.order.paymentAuthorisationFailed", "woa.message.order.paymentAuthorisationFailedReason", {reason: submissionResult.errorMessage});
                    }
                    OrderEvents.orderSubmissionStatus$.next(OrderSubmissionStatus.INCOMPLETE);
                    return undefined;
                }

                // Discard the Tyro pay secret after use
                if (this.paymentProvider === IntegratedPaymentProvider.TYRO) {
                    this.paymentPlatformPublicKey = undefined;
                }

                paymentAuthorisation.paymentAuthorisationType = submissionResult.authorisationType;
                paymentAuthorisation.token = submissionResult.token;
                paymentAuthorisation.paymentCardBrand = submissionResult.brand;
                paymentAuthorisation.paymentCardCountry = submissionResult.country;
                paymentAuthorisation.paymentCardLast4 = submissionResult.last4;
            } catch (error) {
                this.loadingService.dismissSpinner();
                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null, error ? "woa.error.payment.cannotProcessDueToError" : "woa.error.payment.cannotProcess", {"error": error});

                // Rethrow error since errors here are unexpected and should be logged as an anomaly.
                // noinspection ExceptionCaughtLocallyJS
                throw (error);
            }
        } else if (paymentType === PaymentType.APPLE_PAY || paymentType === PaymentType.GOOGLE_PAY) {
            PaymentEvents.isSubmittingWalletPayment$.next(true);

            let phoneNumber: string = this.orderingContextModel.mobilePhone;
            try {
                const submissionResult: WalletPaymentSubmissionResult = await (this.walletPaymentProcessor.canSubmit
                    ? this.walletPaymentProcessor.submit(this._orderModel.orderTotalPayment)
                    : this.navigationService.createPage("woa-wallet-payment-page"));
                if (!submissionResult) {
                    return undefined;
                }
                paymentAuthorisation.paymentAuthorisationType = submissionResult.authorisationType;
                paymentAuthorisation.token = submissionResult.token;

                if (this.requestPayerName && submissionResult.payerName) {
                    console.log("Received payer name from wallet payment:", submissionResult.payerName);
                    const fullName: string = submissionResult.payerName.trim();
                    const names: string[] = fullName.split(" ");
                    if (names.length) {
                        const payerFirstName: string = names[0].trim().substring(0, 30);
                        if (payerFirstName) {
                            this.orderingContextModel.firstName = payerFirstName;
                            this.orderingContextModel.firstNameTrusted = true;
                        }
                        if (names.length > 1) {
                            const i: number = fullName.indexOf(" ") + 1;
                            const payerLastName: string = fullName.trim().substring(i, i + 30);
                            if (payerLastName) {
                                this.orderingContextModel.lastName = payerLastName;
                                this.orderingContextModel.lastNameTrusted = true;
                            }
                        }
                    }
                }
                if (this.requestPayerEmail && submissionResult.payerEmail) {
                    console.log("Received payer email from wallet payment:", submissionResult.payerEmail);
                    this.orderingContextModel.email = submissionResult.payerEmail;
                    this.orderingContextModel.emailTrusted = true;
                }
                if (this.requestPayerPhone && submissionResult.payerPhone) {
                    console.log("Received payer phone from wallet payment:", submissionResult.payerPhone);
                    phoneNumber = submissionResult.payerPhone;
                }
                if (this.requestPayerPostcode && submissionResult.payerPostcode) {
                    console.log("Received payer postcode from wallet payment:", submissionResult.payerPostcode);

                    // This is only captured for opt-in marketing. Never use it instead of the actual postcode already on the address.
                    if (!this.orderingContextModel.address.postCode) {
                        this.orderingContextModel.address.postCode = submissionResult.payerPostcode;
                    }
                }

                // Discard the Tyro pay secret after use
                if (this.paymentProvider === IntegratedPaymentProvider.TYRO) {
                    this.paymentPlatformPublicKey = undefined;
                }
            } catch (error) {
                if (error) {
                    console.error("Wallet payment error", error);
                }

                // noinspection ES6MissingAwait
                this.notificationService.displayAlert(null,
                    !error ? "woa.error.payment.cannotProcess"
                        : !(error instanceof WalletPaymentSubmissionFailure) ? "woa.error.payment.cannotProcessDueToError"
                            : error.errorType === WalletPaymentErrorType.APPLE_PAY_CANCELLED ? "woa.error.payment.applePayCancelled"
                                : error.errorType === WalletPaymentErrorType.GOOGLE_PAY_CANCELLED ? "woa.error.payment.googlePayCancelled"
                                    : error.errorType === WalletPaymentErrorType.PROVIDER_ERROR ? "woa.error.payment.providerError"
                                        : "woa.error.payment.cannotProcess",
                    {
                        "error": error,
                        "providerErrorMessage": error instanceof WalletPaymentSubmissionFailure ? (error.providerErrorMessage || " ") : undefined,
                        "providerErrorCode": error instanceof WalletPaymentSubmissionFailure ? (error.providerErrorCode || " ") : undefined
                    });

                // Error is not rethrown - failures are expected due to cancellation etc., and are handled above.
                return undefined;
            } finally {
                PaymentEvents.isSubmittingWalletPayment$.next(false);
            }

            // For a wallet payment, phone number confirmation happens after we get the payment response, since it is likely to be the source of the phone number
            if (!await this.orderingContextModel.confirmPhoneNumberForSms(phoneNumber, this.orderingContextModel.showPhoneNumber || (this.orderingContextModel.marketingOptIn && this.sessionModel.currentApp.collectMarketingMobilePhone))) {
                // noinspection ES6MissingAwait
                this.analyticsService.trackPlaceOrderAbandoned("integrated payment mobilePhonePrompt");
                if (paymentType === PaymentType.APPLE_PAY) {
                    // noinspection ES6MissingAwait
                    this.notificationService.displayAlert(null, "woa.error.payment.applePayNotCompleted");
                } else if (paymentType === PaymentType.GOOGLE_PAY) {
                    // noinspection ES6MissingAwait
                    this.notificationService.displayAlert(null, "woa.error.payment.googlePayNotCompleted");
                } else {
                    // noinspection ES6MissingAwait
                    this.notificationService.displayAlert(null, "woa.error.payment.notCompleted");
                }
                return undefined;
            }
        }

        return paymentAuthorisation;
    }

    public async handle3DS(action: any, paymentAuthorisation: PaymentAuthorisationData): Promise<ThreeDSResult> {
        let threeDSResult: ThreeDSResult;
        switch (this.paymentProvider) {
            case IntegratedPaymentProvider.STRIPE:
                threeDSResult = await this.loadingService.performWithSpinner(async () => {
                    // noinspection JSUnresolvedReference
                    const result: any = await new (<any>window).Stripe(this.paymentPlatformPublicKey).handleCardAction(action["paymentIntentClientSecret"]);
                    return new ThreeDSResult(!result.error, action["paymentIntentId"], result.error);
                }, 900000);
                break;
            case IntegratedPaymentProvider.OOLIO:
                threeDSResult = await this.navigationService.createPage("woa-adyen-3ds-page", {action: action}) ||
                    new ThreeDSResult(false, undefined, "User cancelled 3DS challenge");
                break;
            default:
                throw ("3DS not supported for " + this.paymentProvider);
        }
        if (threeDSResult.success) {
            // Retry the order placement using the 3DS intent rather than obtaining a new token
            paymentAuthorisation.isThreeDS = true;
            paymentAuthorisation.paymentIntentId = threeDSResult.paymentIntent;
            paymentAuthorisation.token = undefined;
        }
        return threeDSResult;
    }

    public completePayment(paymentAuthorisation: PaymentAuthorisationData): void {
        if (paymentAuthorisation.isThreeDS) {
            // noinspection JSIgnoredPromiseFromCall
            this.analyticsService.track3DSSucceeded(this._orderModel.orderTotalPayment);
        }

        // Save the payment customer id in the browser local storage.
        if (paymentAuthorisation.paymentType === PaymentType.NEW_CREDIT_CARD) {

            // Update or remove the locally persisted payment customer id.
            if (paymentAuthorisation.storePaymentDetails) {
                let paymentCustomerDetails: PaymentCustomerDetails = this.customerDetailsService.loadPaymentCustomer();
                if (!paymentCustomerDetails || paymentCustomerDetails.paymentCustomerId !== this._orderModel.placedOrder.paymentCustomerId) {
                    paymentCustomerDetails = new PaymentCustomerDetails();
                    paymentCustomerDetails.paymentCustomerId = this._orderModel.placedOrder.paymentCustomerId;
                    paymentCustomerDetails.created = new Date();
                    this.customerDetailsService.savePaymentCustomer(paymentCustomerDetails);
                }
            } else {
                this.customerDetailsService.removePaymentCustomer();
            }

            // Once a payment has been completed using a new credit card, the model's copy of the partial card number is discarded
            // to avoid it being presented for use prior to payment details being reloaded to reflect the new (or removed) card.
            this.discardCard();
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/payment/payment.model.ts